/**
 * The `lookup_user` helper behind outbound mentions-as-a-tool (spec §6.1).
 * Ships as a plain builder this milestone — later rollout steps register it
 * as a capability/tool-face (plan Decision 16).
 */
import type { ChannelAdapter } from "../adapter.js";
import type { ActorQuery } from "../types.js";
import type { MentionRenderer } from "./render.js";

/** The `lookup_user` tool shape: query a platform user, get a mention token to paste. */
export type LookupUserTool = {
  name: "lookup_user";
  description: string;
  run(query: ActorQuery): Promise<{ found: boolean; mentionToken?: string; displayName?: string }>;
};

/** Builds the lookup tool over an adapter; later rollout steps register it as a capability/tool-face. */
export function buildLookupUserTool(
  adapter: ChannelAdapter,
  renderer: MentionRenderer,
): LookupUserTool {
  return {
    name: "lookup_user",
    description:
      "Look up a chat-platform user by name, email, or id and return the platform-native mention token for them.",
    async run(query) {
      const actor = await adapter.lookupActor?.(query);
      if (!actor) return { found: false };
      return {
        found: true,
        mentionToken: renderer.renderUserMention(actor),
        displayName: actor.displayName,
      };
    },
  };
}

/** Prompt fragment instructing the model to call lookup_user and paste the token verbatim. */
export const LOOKUP_USER_PROMPT_FRAGMENT =
  "To @-mention a person in the chat channel, first call the lookup_user tool with their name, " +
  "email, or id, then paste the returned mentionToken into your message verbatim — do not alter " +
  "or re-format it. If lookup_user finds nobody, refer to the person by plain name instead.";
